import { useProfileStore } from "@features/profile/profile.store";
import { supabase } from "@shared/lib/supabase/client";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@shared/ui/alert-dialog";
import { Button } from "@shared/ui/button";
import { Card, CardDescription, CardHeader, CardTitle } from "@shared/ui/card";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { toast } from "sonner";

export function DangerZoneCard() {
	const intl = useIntl();
	const profile = useProfileStore((state) => state.profile);
	const navigate = useNavigate();
	const [open, setOpen] = useState(false);
	const [deleting, setDeleting] = useState(false);

	const onDelete = async () => {
		setDeleting(true);
		try {
			await supabase.auth.signOut();
			toast.success(
				intl.formatMessage({
					id: "page.profile.dangerZone.deleteSuccess",
					defaultMessage: "You've been signed out. Contact support to complete deletion.",
				})
			);
			navigate({ to: "/" });
		} catch (err: any) {
			toast.error(
				err.message ??
					intl.formatMessage({
						id: "page.profile.dangerZone.deleteError",
						defaultMessage: "Failed to delete account.",
					})
			);
		} finally {
			setDeleting(false);
			setOpen(false);
		}
	};

	return (
		<Card className="border-destructive/50 bg-destructive/5">
			<CardHeader className="flex justify-between items-center">
				<div className="flex-col">
					<CardTitle className="text-destructive">
						<FormattedMessage
							id="page.profile.dangerZone.title"
							defaultMessage="Delete account"
						/>
					</CardTitle>
					<CardDescription className="text-destructive/80">
						<FormattedMessage
							id="page.profile.dangerZone.description"
							defaultMessage="All your data will be permanently deleted. This action cannot be undone."
						/>
					</CardDescription>
				</div>
				<Button variant="destructive" onClick={() => setOpen(true)}>
					<FormattedMessage
						id="page.profile.dangerZone.button"
						defaultMessage="Delete my account"
					/>
				</Button>
				<AlertDialog open={open} onOpenChange={setOpen}>
					<AlertDialogContent>
						<AlertDialogHeader>
							<AlertDialogTitle>
								<FormattedMessage
									id="page.profile.dangerZone.confirmTitle"
									defaultMessage="Delete your account?"
								/>
							</AlertDialogTitle>
							<AlertDialogDescription>
								<FormattedMessage
									id="page.profile.dangerZone.confirmDescription"
									defaultMessage="This will permanently delete your account ({userId}…) and all associated data. This action cannot be undone."
									values={{ userId: profile?.user_id.slice(0, 8) }}
								/>
							</AlertDialogDescription>
						</AlertDialogHeader>
						<AlertDialogFooter>
							<AlertDialogCancel disabled={deleting}>
								<FormattedMessage id="common.cancel" defaultMessage="Cancel" />
							</AlertDialogCancel>
							<AlertDialogAction
								onClick={(e) => {
									e.preventDefault();
									onDelete();
								}}
								disabled={deleting}
								className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
							>
								{deleting ? (
									<FormattedMessage
										id="page.profile.dangerZone.deleting"
										defaultMessage="Deleting…"
									/>
								) : (
									<FormattedMessage
										id="page.profile.dangerZone.confirmAction"
										defaultMessage="Yes, delete my account"
									/>
								)}
							</AlertDialogAction>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialog>
			</CardHeader>
		</Card>
	);
}
